import { ChangeDetectionStrategy, Component, Input, OnDestroy, OnInit } from '@angular/core'
import { BehaviorSubject } from 'rxjs'
import { filter, switchMap, takeUntil } from 'rxjs/operators'
import { TranslateService } from '@ngx-translate/core'

import { ChatMessageModel, ChatMessageModelType, ChatMessageOption, DELETED_MESSAGE } from '@shared/models/messenger/messenger.model'
import { DestroyService } from '@core/services/destroy.service'
import { MessengerApiService } from '../../services/messenger-api.service'
import { MessengerService } from '../../services/messenger.service'
import { AvatarSize } from '@shared/common-components/avatar/avatar.model'
import { DialogService } from '@shared/dialogs/dialog.service'
import { UploadedFileType } from '@shared/models/uploaded-file'
import { DomSanitizer, SafeUrl } from '@angular/platform-browser'

@Component({
  selector: 'lin-chat-message',
  templateUrl: './chat-message.component.html',
  styleUrls: ['./chat-message.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [DestroyService],
  standalone: false,
})
export class ChatMessageComponent implements OnInit, OnDestroy {
  fileStored: boolean
  imageSrc = new BehaviorSubject<string | ArrayBuffer | SafeUrl>(null)
  imageOriginBlob = new BehaviorSubject<Blob>(null)
  imageOriginSource = new BehaviorSubject<SafeUrl>(null)
  isImageLoaded = new BehaviorSubject<boolean>(false)
  isImageLoading = new BehaviorSubject<boolean>(false)
  AvatarSize = AvatarSize
  ChatMessageOption = ChatMessageOption
  isOriginalLoaded: boolean

  @Input() chatMessage: ChatMessageModel
  @Input() isMyMessage: boolean
  @Input() isGroupChat = false
  @Input() isMyChat = true

  get isAvatar(): boolean {
    return (this.isGroupChat && !this.isMyMessage) || !this.isMyChat
  }

  get isReadAll(): boolean {
    return this.isMyMessage ? this.chatMessage.isReadByOther : this.chatMessage.isRead
  }

  get ngClass(): Record<string, boolean> {
    return { my: this.isMyMessage, system: this.isSystemMessage }
  }

  get isSystemMessage(): boolean {
    return this.chatMessage.type === ChatMessageModelType.System && !this.isDeletedMessage
  }

  get isDeletedMessage(): boolean {
    return this.chatMessage.event === DELETED_MESSAGE
  }

  get isEditAvailable(): boolean {
    return this.isMyMessage && !this.isDeletedMessage
  }

  get isImageFile(): boolean {
    return this.chatMessage.file?.mimeType?.includes('image/')
  }

  get isOpenImageOverlay(): boolean {
    return this._isOpenImageOverlay
  }

  set isOpenImageOverlay(value: boolean) {
    this._isOpenImageOverlay = value
    value ? this.contentElement.classList.add('content-overlay') : this.contentElement.classList.remove('content-overlay')
  }

  private _isOpenImageOverlay: boolean
  private linkFile: HTMLAnchorElement
  private contentElement: HTMLElement

  constructor(
    private readonly messengerApiService: MessengerApiService,
    private readonly messengerService: MessengerService,
    private readonly dialog: DialogService,
    private readonly translate: TranslateService,
    private readonly destroyService: DestroyService,
    private readonly sanitizer: DomSanitizer,
  ) {}

  ngOnInit(): void {
    this.fileStored = this.chatMessage?.file?.isStored

    if (!this.fileStored) return

    const fileUrl = this.chatMessage?.file?.url
    this.contentElement = document.querySelector('lin-content')

    this.loadImageFile(fileUrl)
  }

  handleImageLoad(): void {
    const fileUrl = this.chatMessage?.file?.original?.url

    this.loadImageFile(fileUrl)
  }

  handleImageClick(): void {
    this.isOpenImageOverlay = !this.isOpenImageOverlay
    !this.isOriginalLoaded && this.handleImageLoad()
  }

  downloadFile(isOriginal = false): void {
    if (!this.fileStored) return

    this.isImageLoading.next(true)
    const { file } = this.chatMessage

    if (this.linkFile?.href && this.isOriginalLoaded) {
      this.isImageLoading.next(false)
      this.linkFile.click()

      return
    }

    const getFileSourceSubscription = (source: Blob): void => {
      const blob = new Blob([source])
      this.createDownloadLink(blob, true)
      this.imageOriginBlob.next(blob)

      if (file.type === UploadedFileType.Image) {
        const objectURL = URL.createObjectURL(blob)
        const imageOriginSource = this.sanitizer.bypassSecurityTrustUrl(objectURL)
        this.imageOriginSource.next(imageOriginSource)
        this.isOriginalLoaded = true
      }

      this.isImageLoading.next(false)
      this.linkFile.click()
    }

    const fileUrl = isOriginal ? file.original.url : file.url

    this.messengerApiService.getFileSource(fileUrl).pipe(takeUntil(this.destroyService)).subscribe(getFileSourceSubscription)
  }

  handleMessageOptions(option: ChatMessageOption): void {
    switch (option) {
      case ChatMessageOption.EDIT: {
        this.messengerService.setChatMessage(<Pick<ChatMessageModel, 'id' | 'message'>>{
          id: this.chatMessage.id,
          message: this.chatMessage.message,
        })

        return
      }

      case ChatMessageOption.DELETE_ATTACHMENT: {
        const modalMessage = <string>this.translate.instant('CLIENT.MESSENGER.CHAT_DETAILS.DELETE_ATTACHMENT_TITLE')
        this.dialog
          .confirm(modalMessage)
          .afterClosed()
          .pipe(
            takeUntil(this.destroyService),
            filter(Boolean),
            switchMap(() =>
              this.messengerApiService.deleteAttachment(this.chatMessage.chat.id, this.chatMessage.id, this.chatMessage.file.id),
            ),
          )
          .subscribe()

        return
      }

      case ChatMessageOption.DELETE: {
        const modalMessage = <string>this.translate.instant('CLIENT.MESSENGER.CHAT_DETAILS.DELETE_MESSAGE_TITLE')
        this.dialog
          .confirm(modalMessage)
          .afterClosed()
          .pipe(
            takeUntil(this.destroyService),
            filter(Boolean),
            switchMap(() => this.messengerApiService.deleteChatMessage(this.chatMessage.chat.id, this.chatMessage.id)),
          )
          .subscribe()

        return
      }

      default:
        return
    }
  }

  ngOnDestroy(): void {
    this.linkFile?.remove()
  }

  private createDownloadLink(blob: Blob, isOriginal: boolean): void {
    if (this.linkFile) return
    this.linkFile = document.createElement('a')
    this.linkFile.href = URL.createObjectURL(blob)
    this.linkFile.target = '_blank'
    this.isOriginalLoaded = isOriginal
    this.linkFile.download = `${this.chatMessage.file.displayName}`

    document.body.appendChild(this.linkFile)
  }

  private loadImageFile(fileUrl: string, isOriginal?: boolean): string {
    if (!fileUrl) return

    this.isImageLoading.next(true)
    this.messengerApiService
      .getFileSource(fileUrl)
      .pipe(takeUntil(this.destroyService))
      .subscribe((blob: Blob): void => {
        const objectURL = URL.createObjectURL(blob)
        const imageOriginSource = this.sanitizer.bypassSecurityTrustUrl(objectURL)

        if (isOriginal) {
          this.imageOriginSource.next(imageOriginSource)
          this.createDownloadLink(blob, isOriginal)
        } else {
          this.imageSrc.next(imageOriginSource)
          this.messengerService.triggerScrollToBottom()
        }

        this.isOriginalLoaded = isOriginal

        this.isImageLoaded.next(true)
        this.isImageLoading.next(false)
      })
  }
}
